import _ from "lodash";
import React, { useState, memo, useEffect, useRef, useMemo } from "react";

// design system
import { colors } from "@toddle-edu/ds-theme";
import { Avatar, Button, IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  PencilLineOutlined,
  SquareFilled,
  VolumeHighOutlined,
  StarsTwoFilled,
  RefreshArrowOutlined,
  DeleteOutlined,
  ReloadArrowOutlined,
} from "@toddle-edu/ds-icons";

// components
import FullScreenLoader from "UIComponents/FullScreenLoader";
import I18nHOC from "UIComponents/I18nHOC";
import ChatAttachmentsViewer from "UIComponents/ChatAttachmentsViewer";
import MarkdownView from "UIComponents/MarkdownView";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import AITutorAnalysis from "AppComponents/AITutorAnalysis";
import AITutorChatBar from "../AITutorChatBar";
import TheatreModeV2 from "UIComponents/TheatreModeV2";
import withPusherBind from "UIComponents/PusherConsumerHoc";

// third party libs
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classNames from "classnames";

// modules
import {
  onSendQuery,
  updateAITutorMessage,
  evaluateAITutorConversation,
} from "../../modules/AITutorChatModule";
import { getTextToSpeechAudio, hideIntercom } from "modules/Services";
import {
  getUserType,
  getCurrentCurriculumProgramType,
  getIsStaff,
} from "modules/CommonSelectors";
import ACLStore from "lib/aclStore";
import { setGlobalTrayEnabledState } from "Login/modules/LoginModule";

// utils
import {
  checkScrollAtBottom,
  scrollIntoView,
  isIdEqual,
  htmlToText,
} from "Utils";
import { getHasHtmlCode } from "Utils/StringHelpers";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import {
  isUserEngagedWithAI,
  extractContentAndCode,
  getCleanedContent,
  extractStateAndContent,
  isPlayMode,
} from "../../modules/Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getUserBasicDetailsFromCache } from "modules/CommonGraphqlHelpers";
import {
  getCommonMarkdownComponents,
  getAssessmentFieldValue,
  replaceImgWithInlineLatex,
} from "Utils/AIUtils";

// constants
import {
  WRITING,
  STREAMING,
  PLANNER,
  GENERATING_EVALUATION,
  THINKING,
} from "Constants/stringConstants";
import { ATTACHMENT_OMIT_ATTRIBUTES } from "store/static";
import { getIsAITutorEnhancementsV2Enabled } from "Utils/ACLStoreUtils";

// scss
import classes from "./AITutorChat.scss";

const styles = {
  aiFeedbackStrip: {
    background: colors.surfaceWhite,
    padding: 0,
    borderRadius: "8px",
  },
  iconStyle: {
    alignSelf: "flex-start",
  },
  audioButtonStyle: {
    alignSelf: "flex-start",
  },
  innerContainerStyle: {
    padding: "16px",
  },
  evaluationContainerStyle: {
    padding: "12px 0px",
  },
};

const aiStateConfig = {
  WRITING: {
    label: "classRoom:writing",
  },
  GENERATING_EVALUATION: {
    label: "ai:evaluating_this_conversation",
  },
  ANALYSING: {
    label: "ai:analysing_label",
  },
  THINKING: {
    label: "ai:thinking",
  },
};

const AITutorChat = memo(props => {
  const {
    t,
    entityId,
    messages,
    onSendQuery,
    evaluateAITutorConversation,
    containerStyle,
    aiConversationId,
    assessmentDetails,
    isEditMode,
    course,
    entityType,
    isStudent,
    gradeId,
    userType,
    selectedTab,
    context,
    isStaff,
    onClickStartNewSession,
    source,
    isLibrary,
    isActivityLog,
    chatBarProps = {},
    updateAITutorMessage,
    shouldShowEditAndDeleteIcon,
    analysisToolEvaluation,
    aiAnalysisCriteria,
    userBasicDetails,
    currentAIState,
    updateCurrentAIState,
    updateIsAnalysing,
    isAnalysing,
    testId,
    learningCourseId,
    showCompactUI,
    isEvaluating,
    setIsEvaluating,
    flaggedMessagesCount,
    currentlyAudioPlayingForItemId,
    isAudioGenerating,
    onSpeakMessageClick,
  } = props;

  // ------------------ states -----------------------

  const [query, setQuery] = useState("");
  const [attachmentItems, setAttachmentItems] = useState({});
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [mediaViewerData, setMediaViewerModalState] = useState(null);
  const [editMessageId, setEditMessageId] = useState(null);

  const [audioTranscripts, setAudioTranscripts] = useState([]);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  // ------------------ refs -----------------------

  // ref
  const scrollEndRef = useRef(null);
  const scrollRef = useRef(null);

  // ------------------ side effects -----------------------

  // on message sent, scroll to bottom
  useEffect(() => {
    if (!_.isEmpty(messages)) scrollToBottom();
  }, [messages, shouldScrollToBottom, currentAIState]);

  useEffect(() => {
    const { hideIntercom, setGlobalTrayEnabledState } = props;
    if (_.isEmpty(mediaViewerData)) {
      hideIntercom();
      setGlobalTrayEnabledState(false);
    }
  }, [mediaViewerData]);

  // ------------------ functions -----------------------
  const allFields = _.get(assessmentDetails, "allFields", []);

  const studentCommunicationMode = getAssessmentFieldValue({
    fields: allFields,
    uid: "studentCommunicationMode",
  });

  const tutorCommunicationMode = getAssessmentFieldValue({
    fields: allFields,
    uid: "tutorCommunicationMode",
  });

  const isSpeakingOnly = studentCommunicationMode === "speaking_only";

  const isAudioOnly = tutorCommunicationMode === "speaking_only";
  const isWritingOnly = tutorCommunicationMode === "writing_only";

  const userAudioMessageCount = useMemo(() => {
    return _.filter(messages, ({ node }) => {
      const isUserMessage = node?.authorType === "user";
      const hasAudioAttachment = _.some(node?.attachments, { type: "AUDIO" });
      return isUserMessage && hasAudioAttachment;
    }).length;
  }, [messages]);

  // show the message on chatbar
  const onEditIconClick = id => {
    const editedMessage = _.find(
      messages,
      message => _.get(message, "node.id") === id
    );

    if (editedMessage) {
      const messageNode = _.get(editedMessage, "node", {});

      // get query and code content
      const { content, code } = extractContentAndCode(messageNode?.content);

      // initial attachment object
      const attachmentItems = {
        ATTACHMENTS: [],
        CODE: code,
        GRAPH: null,
      };

      // populate the attachment object
      const attachments = messageNode.attachments || [];

      _.map(attachments, attachment => {
        const isGraph = _.get(attachment, "metadata.isGraph", false);
        if (isGraph) {
          attachmentItems.GRAPH = attachment;
        } else {
          attachmentItems.ATTACHMENTS.push(attachment);
        }
      });

      // update the states
      setQuery(_.trim(content) || "");
      setAttachmentItems(attachmentItems);
      setEditMessageId(id); // store the id of the message being edited
    }
  };

  // delete the message
  const onDeleteIconClick = async id => {
    await updateAITutorMessage({
      variables: {
        input: {
          id,
          isArchived: true,
        },
      },
    });
  };

  // render edit icon on hover
  const renderEditAndDeleteIcon = id => (
    <div className={classes.editIconContainer}>
      <div className={classes.iconsContainer}>
        <Tooltip
          tooltip={t("common:edit_with_label", {
            label: t("common:message"),
          })}
          dsVersion="2.0"
          showArrow={true}
        >
          <IconButton
            dsVersion="2.0"
            icon={<PencilLineOutlined />}
            variant={"neutral"}
            size={"small"}
            onClick={() => onEditIconClick(id)}
            style={styles.iconStyle}
            testId={`${testId}-aiTutorChat-editIcon`}
          />
        </Tooltip>

        <Tooltip
          tooltip={t("common:delete_message")}
          dsVersion="2.0"
          showArrow={true}
        >
          <IconButton
            dsVersion="2.0"
            icon={<DeleteOutlined />}
            variant={"neutral"}
            size={"small"}
            onClick={() => onDeleteIconClick(id)}
            style={styles.iconStyle}
            testId={`${testId}-aiTutorChat-deleteIcon`}
          />
        </Tooltip>
      </div>
    </div>
  );

  // scroll to the end of the chat
  const scrollToBottom = () => {
    // when opening message is added then no need to scroll, as user will see the message from the top
    const isOpeningMessageOnly =
      _.size(messages) === 1 &&
      _.get(messages, "0.node.metadata.isOpeningMessage", false);

    const isSampleConversation = _.some(aiAnalysisCriteria, {
      id: selectedTab,
    });

    if (
      scrollEndRef.current &&
      isEditMode &&
      !isOpeningMessageOnly &&
      (!isSampleConversation ||
        _.every(messages, ({ node }) => _.includes(node?.id, "NEW_"))) &&
      shouldScrollToBottom
    ) {
      scrollIntoView({ element: scrollEndRef.current });
    }
  };

  // send message on enter

  // empty spaces, new lines are represented as "&nbsp;", "<br>", "\n" in contenteditable
  // so simple trim will not work, hence we need to convert the html to text and then trim
  const isQueryEmpty = _.isEmpty(
    _.trim(
      htmlToText(query, {
        preserveNewlines: false,
        wordwrap: null,
      })
    )
  );

  const isAttachmentEmpty = _.every(attachmentItems, item => _.isEmpty(item));

  // if user uploads an audio from where we could not extract transcript then also we will consider it as empty
  // example - audio less than <= 1 sec
  const attachments = _.get(attachmentItems, "ATTACHMENTS", []);

  const isAudioTranscriptEmpty =
    _.isEmpty(audioTranscripts) &&
    _.size(attachments) === 1 &&
    _.get(attachments, "[0].type") === "AUDIO";

  const isAIChatStreaming = _.includes(
    [WRITING, STREAMING, GENERATING_EVALUATION, THINKING],
    currentAIState
  );

  const isSendDisabled =
    ((isSpeakingOnly || isQueryEmpty) &&
      (isAttachmentEmpty ||
        (isAudioTranscriptEmpty && !getIsAITutorEnhancementsV2Enabled()))) ||
    isAIChatStreaming;

  const onKeyDown = ({ e, audioAttachments }) => {
    const isEnterPressed = e.keyCode === 13;
    const isShiftPressed = e.shiftKey;

    if (isEnterPressed && !isShiftPressed && !isSendDisabled) {
      e.preventDefault();

      if (editMessageId) {
        onClickEditMessage({ audioAttachments });
      } else {
        onSendMessage({ audioAttachments });
      }
    }
  };

  // return added attachments
  const getAddedAttachments = ({ audioAttachments } = {}) => {
    return _.reduce(
      attachmentItems,
      (acc, value, key) => {
        if (_.includes(["ATTACHMENTS", "GRAPH"], key) && !_.isEmpty(value)) {
          const updatedAttachments = _.map(_.castArray(value), attachment => {
            const _attachment =
              audioAttachments?.[attachment?.id] || attachment;
            return _.omit(_attachment, ATTACHMENT_OMIT_ATTRIBUTES);
          });

          acc.push(...updatedAttachments);
        }
        return acc;
      },
      []
    );
  };

  // handle send button click
  const onSendMessage = async (params = {}) => {
    // there are 3 attachmentItems (ATTACHMENTS, GRAPH, CODE)
    // ATTACHMENTS and GRAPH will contain attachment media

    const attachments = getAddedAttachments(params);

    await onSendQuery({
      t,
      query,
      setQuery,
      entityId,
      aiConversationId,
      course,
      entityType,
      gradeId,
      attachments,
      attachmentItems,
      setAttachmentItems,
      context,
      assessmentDetails,
      audioTranscripts: _.map(audioTranscripts, "transcript"),
      setAudioTranscripts,
      selectedTab,
      updateCurrentAIState,
      updateIsAnalysing,
      source,
      setIsEvaluating,
      flaggedMessagesCount,
      learningCourseId,
      onSpeakMessageClick,
    });
  };

  const resetChatBarStates = () => {
    setEditMessageId(null);
    setQuery("");
    setAttachmentItems({});
  };

  const onClickEditMessage = async params => {
    if (!editMessageId) return;

    const originalAttachments = _.get(
      _.find(messages, ({ node }) => node?.id === editMessageId),
      "node.attachments",
      []
    );
    const updatedAttachments = getAddedAttachments(params);

    const removedAttachments = _.differenceBy(
      originalAttachments,
      updatedAttachments,
      "id"
    );

    const addedAttachments = _.differenceBy(
      updatedAttachments,
      originalAttachments,
      "id"
    );

    const updatedContent = replaceImgWithInlineLatex({
      query,
      attachmentItems,
    });

    await updateAITutorMessage({
      variables: {
        input: {
          id: editMessageId,
          content: updatedContent,
          addedAttachments,
          removedAttachments: _.map(removedAttachments, "id"),
        },
      },
    });

    resetChatBarStates();
  };

  // evaluate  the user conversation
  const onClickEvaluateConversation = async () => {
    setIsEvaluating(true);

    await evaluateAITutorConversation({
      course,
      entityId,
      gradeId,
      aiConversationId,
      assessmentDetails,
      messages,
      setCurrentAIState: value => updateCurrentAIState(selectedTab, value),
      shouldCreateEvaluationMessage:
        source === "PREVIEW_CHAT" && isPlayMode(selectedTab),
      learningCourseId,
    });

    setIsEvaluating(false);
  };

  // check if the scroll is at bottom
  const onScrollBodyContainer = e => {
    setShouldScrollToBottom(checkScrollAtBottom(e.target, 40));
  };

  const handleStartNewSession = () => {
    flaggedMessagesCount.current = 0;
    onClickStartNewSession();
  };

  const renderChatBody = () => {
    const assessmentId = _.get(assessmentDetails, "id");
    const markdownComponents = getCommonMarkdownComponents();

    const lastAssistantMessageIndex = _.findLastIndex(
      messages,
      ({ node }) => node?.authorType === "assistant"
    );

    const isLastMessageFromUser =
      _.get(_.last(messages), "node.authorType") === "user";

    return _.map(messages, ({ node }, index) => {
      const {
        id,
        authorType,
        createdBy,
        content,
        metadata,
        usageMetricsId,
        attachments,
      } = node || {};
      const { profileImage } = createdBy || {};

      const isLastMessage = index === _.size(messages) - 1;
      const isLastAssistantMessage = index === lastAssistantMessageIndex;

      const showAnalysingForSelectedTab =
        isAnalysing?.[selectedTab] && isLastMessage;

      const user =
        isLibrary && !isPlayMode(selectedTab) ? userBasicDetails : createdBy;
      const name = getFormattedUserName({ user, type: userType });

      const totalAttachments = _.size(attachments);

      const onMediaClick = selectedAttachment => {
        const activeAttachmentIndex = _.findIndex(attachments, attachment =>
          isIdEqual(attachment.id, selectedAttachment.id)
        );

        setMediaViewerModalState({
          attachments,
          activeAttachmentIndex,
        });
      };

      const isCurrentAudioPlaying = currentlyAudioPlayingForItemId == id;

      const isAudioDisabled =
        _.includes(id, "NEW") ||
        (_.includes([STREAMING, THINKING], currentAIState) && isLastMessage) ||
        (isAudioGenerating && isCurrentAudioPlaying);

      const handleSpeakMessageClick = () => {
        onSpeakMessageClick(node);
      };

      if (_.isEmpty(_.trim(content)) && totalAttachments === 0) return null;

      switch (authorType) {
        case "user": {
          const userChatClass = classNames(classes.userChat, "text-body-2", {
            [classes.widthMinContent]:
              totalAttachments > 0 && !_.some(attachments, { type: "AUDIO" }),
            [classes.userChatLibrary]: isLibrary || isActivityLog,
          });

          // as id changes due to optimistic response (NEW_ to actual id), for audio attachment
          // this causes audio player (wavesurfer) visualisation to re-render and flicker
          const audioAttachment = _.find(attachments, { type: "AUDIO" });
          const uniqueId = !_.isEmpty(audioAttachment)
            ? audioAttachment?.url
            : id;

          return (
            <div className={classes.userChatBox} key={uniqueId}>
              <div className={classes.userChatBoxWithEditIcon}>
                {shouldShowEditAndDeleteIcon
                  ? renderEditAndDeleteIcon(id)
                  : null}
                <div className={userChatClass} id={`${id}_AI_MESSAGE`}>
                  {content ? (
                    <MarkdownView
                      content={content}
                      components={markdownComponents}
                      contentContainerClass={classes.contentContainerClass}
                    />
                  ) : null}

                  {totalAttachments ? (
                    <div className={classes.messageAttachmentContainer}>
                      <ChatAttachmentsViewer
                        totalAttachments={totalAttachments}
                        attachments={attachments}
                        onMediaClick={onMediaClick}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
              <Avatar
                size="small"
                shape="circle"
                shade="light"
                color="yellow"
                className={classes.noShrink}
                name={name}
                src={profileImage}
                dsVersion={"2.0"}
              />
            </div>
          );
        }
        case "assistant": {
          const isOpeningMessage = _.get(metadata, "isOpeningMessage", false);
          let cleanedContent =
            isLibrary && isOpeningMessage
              ? getCleanedContent(content)
              : content;

          if (_.isString(cleanedContent)) {
            const { content: extractedContent, output: outputTagContent } =
              extractStateAndContent(content) || {};
            cleanedContent = extractedContent || outputTagContent;
          }

          // for audio only tutor, hide the assistant message box till the audio starts playing
          if (
            isAudioOnly &&
            isLastAssistantMessage &&
            !isLastMessageFromUser &&
            !_.isEmpty(currentAIState)
          ) {
            return null;
          }

          return cleanedContent ? (
            <AIChatBox
              t={t}
              id={id}
              key={id}
              isStaff={isStaff}
              content={cleanedContent}
              usageMetricsId={usageMetricsId}
              metadata={metadata}
              isLibrary={isLibrary}
              isActivityLog={isActivityLog}
              renderEditAndDeleteIcon={renderEditAndDeleteIcon}
              shouldShowEditAndDeleteIcon={shouldShowEditAndDeleteIcon}
              onSpeakMessageClick={handleSpeakMessageClick}
              isAudioDisabled={isAudioDisabled}
              isCurrentAudioPlaying={isCurrentAudioPlaying}
              totalAttachments={totalAttachments}
              attachments={attachments}
              onMediaClick={onMediaClick}
              showAnalysingForSelectedTab={showAnalysingForSelectedTab}
              testId={testId}
              isSpeakingOnly={isSpeakingOnly}
              isAudioOnly={isAudioOnly}
              isWritingOnly={isWritingOnly}
            />
          ) : null;
        }
        case "flaggedMessage":
        case "tutorUpdated": {
          const message = _.includes(content, "ai:") ? t(content) : content;

          const tutorUpdatedTextClass = classNames(
            classes.tutorUpdatedText,
            "text-body-s",
            { [classes.italic]: authorType === "flaggedMessage" }
          );

          const textContainerClass = classNames({
            [classes.tutorUpdated]: true,
            [classes.flaggedMessage]: authorType === "flaggedMessage",
          });

          return (
            <div key={id} className={classes.tutorUpdatedContainer}>
              <span className={classes.line} />
              <span className={textContainerClass}>
                <span className={tutorUpdatedTextClass}>{message}</span>
              </span>
              <span className={classes.line} />
            </div>
          );
        }
        case "tutorUpdatedWithResetChat": {
          const onClickResetChat = async () => {
            setShowFullScreenLoader(true);
            flaggedMessagesCount.current = 0;
            await onClickStartNewSession();
            setShowFullScreenLoader(false);
          };

          return (
            <div key={id} className={classes.tutorUpdatedContainer}>
              <span className={classes.line} />
              <span className={classes.tutorUpdated}>
                <span
                  className={classNames(
                    classes.tutorUpdatedText,
                    "text-body-s"
                  )}
                >
                  {t(content)}
                </span>
                <span className={classes.dot} />

                <Button
                  dsVersion={"2.0"}
                  icon={<ReloadArrowOutlined />}
                  variant={"progressive"}
                  type={"inline"}
                  size={"small"}
                  onClick={onClickResetChat}
                  disabled={!isEditMode}
                  testId={`${id}-aiTutorChat-tutorUpdatedWithResetChat`}
                >
                  {t("ai:reset_chat")}
                </Button>
              </span>
              <span className={classes.line} />
            </div>
          );
        }
        case "aiTutorEvaluation": {
          const onRegenerateClick = async () => {
            await onDeleteIconClick(id);
            await onClickEvaluateConversation();
          };

          return (
            <div key={id} className={classes.aiTutorEvaluationChatBox}>
              <Avatar
                size={"small"}
                shape={"circle"}
                shade={"dark"}
                className={classes.noShrink}
                name={"A I"}
                color={"pink"}
                dsVersion={"2.0"}
              />
              <div className={classes.aiTutorEvaluationContainer}>
                <div className={classes.aiTutorEvaluation}>
                  <AITutorAnalysis
                    assessmentId={assessmentId}
                    aiConversationId={aiConversationId}
                    innerContainerStyle={styles.innerContainerStyle}
                    evaluationContainerStyle={styles.evaluationContainerStyle}
                    source={source}
                    onRegenerateClick={onRegenerateClick}
                  />
                  <div className={classes.feedBackStripContainer}>
                    <AIFeedbackStrip
                      showWarningText={false}
                      containerStyle={styles.aiFeedbackStrip}
                      usageMetricsId={usageMetricsId}
                      {...metadata}
                      sourceFrom={"LESSON_PLANNING_ASSISTANT"}
                      showLikeButton={false}
                      showDislikeButton={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        }
        default:
          return null;
      }
    });
  };

  // render attachments through theatre mode
  const renderMediaViewer = () => {
    if (_.isEmpty(mediaViewerData)) {
      return null;
    }

    const { attachments, activeAttachmentIndex } = mediaViewerData;

    const closePreviewModal = () => {
      setMediaViewerModalState(null);
    };

    return (
      <TheatreModeV2
        mediaAttachments={{ attachments }}
        activeAttachmentIndex={activeAttachmentIndex}
        toggleTheatreMode={closePreviewModal}
      />
    );
  };

  // ------------------ variables -----------------------

  const hasFlaggedMessageDisclaimer = _.some(messages, {
    node: { authorType: "flaggedMessage" },
  });

  const showFooter =
    isEditMode &&
    !hasFlaggedMessageDisclaimer &&
    (!selectedTab || isPlayMode(selectedTab) || shouldShowEditAndDeleteIcon) &&
    _.isEmpty(_.first(analysisToolEvaluation)?.toolResponses) &&
    !isEvaluating;

  const showGenerateEvaluationButton =
    isUserEngagedWithAI(messages, 1) &&
    !hasFlaggedMessageDisclaimer &&
    isStaff &&
    !_.includes(
      [WRITING, STREAMING, GENERATING_EVALUATION, THINKING],
      currentAIState
    ) &&
    source === "PREVIEW_CHAT";

  const showStartNewChatButton =
    !_.isEmpty(_.first(analysisToolEvaluation)?.toolResponses) &&
    !isEvaluating &&
    source === "PREVIEW_CHAT" &&
    isPlayMode(selectedTab);

  const showAICanMakeMistakesMsg =
    !hasFlaggedMessageDisclaimer && (isStudent || source === "STUDENT_PREVIEW");

  const currentState = aiStateConfig[currentAIState];

  // ------------------ classes -----------------------

  const studentInfoTextClass = classNames(
    "text-body-xs",
    classes.studentInfoText
  );

  const chatBodyContainerClass = classNames({
    [classes.chatBodyContainer]: true,
    [classes.studentViewModeChatBodyContainer]:
      showAICanMakeMistakesMsg && !isEditMode,
  });

  const infoTextClass = classNames({
    [classes.infoTextContainer]: true,
    [classes.infoTextViewModeContainer]:
      showAICanMakeMistakesMsg && !isEditMode,
  });

  // ------------------ jsx -----------------------

  const infoText = (
    <div className={infoTextClass}>
      <div className={studentInfoTextClass}>{t("ai:tutor_chat_modal_msg")}</div>
    </div>
  );

  return (
    <div className={classes.chatContainer} style={containerStyle}>
      <div
        className={chatBodyContainerClass}
        onScroll={onScrollBodyContainer}
        ref={scrollRef}
      >
        {renderChatBody()}

        {currentState ? (
          <AIChatBox
            content={
              <div className={classes.generatingMsg}>
                <div className={classes.generatingSingleMsg}>
                  {t(currentState.label)}
                </div>
                <div className={classes.dotsAnimation} />
              </div>
            }
            shouldRenderMarkdown={false}
            isLibrary={isLibrary}
            isActivityLog={isActivityLog}
            currentAIState={currentAIState}
          />
        ) : null}
        {isEditMode ? (
          <div className={classes.scrollEnd} ref={scrollEndRef} />
        ) : null}
        {showAICanMakeMistakesMsg && !isEditMode ? infoText : null}
      </div>

      {/* Footer */}
      {showFooter ? (
        <div className={classes.chatBarContainer}>
          <AITutorChatBar
            value={query}
            entityId={entityId}
            entityType={entityType}
            updateInputField={setQuery}
            attachmentItems={attachmentItems}
            updateAttachmentItems={setAttachmentItems}
            audioTranscripts={audioTranscripts}
            updateAudioTranscripts={setAudioTranscripts}
            onKeyDown={onKeyDown}
            onSendMessage={onSendMessage}
            aiConversationId={aiConversationId}
            assessmentDetails={assessmentDetails}
            isSendDisabled={isSendDisabled}
            isEditMode={!_.isEmpty(editMessageId)}
            setEditMessageId={setEditMessageId}
            onClickEditMessage={onClickEditMessage}
            source={source}
            testId={testId}
            showCompactUI={showCompactUI}
            gradeId={gradeId}
            isSpeakingOnly={isSpeakingOnly}
            userAudioMessageCount={userAudioMessageCount}
            {...chatBarProps}
          />

          {showGenerateEvaluationButton ? (
            <Button
              dsVersion={"2.0"}
              variant={"neutral"}
              type={"outlined"}
              icon={<StarsTwoFilled />}
              className={classes.generateAIAnalysisBtn}
              onClick={onClickEvaluateConversation}
              testId={`${testId}-aiTutorChat-generateAIAnalysisBtn`}
            >
              {t("ai:generate_ai_analysis")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {showStartNewChatButton ? (
        <div className={classes.buttonWrapper}>
          <Button
            dsVersion={"2.0"}
            variant={"neutral"}
            type={"outlined"}
            size={"medium"}
            onClick={handleStartNewSession}
            isFullWidth={true}
            testId={`${testId}-aiTutorChat-startNewChatButton`}
          >
            <div className={classes.startNewChatButton}>
              <RefreshArrowOutlined size="xx-small" type="outline" />
              <div className="text-label-l">
                {t("classRoom:start_new_conversation")}
              </div>
            </div>
          </Button>
        </div>
      ) : null}

      {showAICanMakeMistakesMsg && isEditMode ? infoText : null}

      {/* Opening media in theatre mode */}
      {renderMediaViewer()}

      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </div>
  );
});

AITutorChat.displayName = "AITutorChat";

const AIChatBox = props => {
  const {
    t,
    id,
    ref,
    content,
    usageMetricsId,
    metadata,
    shouldRenderMarkdown = true,
    isLibrary,
    isActivityLog,
    renderEditAndDeleteIcon = _.noop,
    shouldShowEditAndDeleteIcon,
    onSpeakMessageClick,
    isAudioDisabled,
    isCurrentAudioPlaying,
    totalAttachments,
    attachments,
    onMediaClick,
    currentAIState,
    showAnalysingForSelectedTab,
    testId,
    isAudioOnly,
    isWritingOnly,
  } = props;

  const { model, temperature, intentUsageMetricsId, isOpeningMessage = false } =
    metadata || {};

  const contentContainerMarkdownClass = classNames(
    "text-body-2",
    classes.markdownContainer,
    classes.assistantChat,
    {
      [classes.assistantChatLibrary]: isLibrary,
    },
    {
      [classes.assistantChatActivityLog]: isActivityLog,
    },
    {
      [classes.audioOnly]: isAudioOnly,
    }
  );

  const contentContainerClass = classNames(
    "text-body-2",
    classes.assistantChat,
    {
      [classes.assistantChatLibrary]: isLibrary,
      [classes.assistantChatActivityLog]: isActivityLog,
      [classes.whiteBackground]: currentAIState === GENERATING_EVALUATION,
    }
  );

  const isHTML = isOpeningMessage && getHasHtmlCode({ message: content });

  const audioIcon = isCurrentAudioPlaying ? (
    <SquareFilled size="xxx-small" />
  ) : (
    <VolumeHighOutlined size="xxx-small" />
  );

  const markdownComponents = getCommonMarkdownComponents();

  return (
    <div className={classes.assistantChatBox} ref={ref}>
      <Avatar
        size={"small"}
        shape={"circle"}
        shade={"dark"}
        className={classes.noShrink}
        name={"A I"}
        color={"pink"}
        dsVersion={"2.0"}
      />

      <div className={classes.assistantChatBoxWithEditIcon}>
        {shouldRenderMarkdown ? (
          <div
            className={contentContainerMarkdownClass}
            id={`${id}_AI_MESSAGE`}
          >
            <div className={classes.assistantChatWithAudio}>
              {!isAudioOnly ? (
                <>
                  <div className={classes.blockContentText}>
                    <MarkdownView
                      content={content}
                      components={markdownComponents}
                      contentContainerClass={classes.contentContainerClass}
                      enableHTMLrendering={isHTML}
                      enableLatexSanitization={true}
                    />
                    {showAnalysingForSelectedTab ? (
                      <div className={classes.analysingMsg}>
                        <div className={classes.generatingSingleMsg}>
                          {t("worksheet:analysing")}
                        </div>
                        <div className={classes.dotsAnimation} />
                      </div>
                    ) : null}
                  </div>

                  {!isWritingOnly ? (
                    <ConditionalWrapper
                      type={"wrapperAsElement"}
                      condition={!isAudioDisabled && !isCurrentAudioPlaying}
                      wrapper={
                        <Tooltip
                          dsVersion="2.0"
                          showArrow={true}
                          tooltip={t("common:read_aloud")}
                        />
                      }
                    >
                      <IconButton
                        dsVersion={"2.0"}
                        type={"plain"}
                        variant={"neutral"}
                        size={"small"}
                        icon={audioIcon}
                        style={styles.audioButtonStyle}
                        disabled={isAudioDisabled}
                        onClick={onSpeakMessageClick}
                        testId={`${testId}-aiTutorChat-audioIcon`}
                      />
                    </ConditionalWrapper>
                  ) : null}
                </>
              ) : (
                <Button
                  variant={"neutral"}
                  type={"outlined"}
                  icon={
                    isCurrentAudioPlaying ? (
                      <SquareFilled />
                    ) : (
                      <VolumeHighOutlined />
                    )
                  }
                  dsVersion={"2.0"}
                  size={"medium"}
                  onClick={onSpeakMessageClick}
                  testId={`${testId}-aiTutorChat-audio-button`}
                  disabled={isAudioDisabled}
                >
                  {isCurrentAudioPlaying
                    ? t("common:stop")
                    : t("common:listen")}
                </Button>
              )}
            </div>

            {totalAttachments ? (
              <div className={classes.messageAttachmentContainer}>
                <ChatAttachmentsViewer
                  totalAttachments={totalAttachments}
                  attachments={attachments}
                  onMediaClick={onMediaClick}
                />
              </div>
            ) : null}

            <div className={classes.feedBackStripContainer}>
              <AIFeedbackStrip
                showWarningText={false}
                containerStyle={styles.aiFeedbackStrip}
                usageMetricsId={usageMetricsId}
                model={model}
                temperature={temperature}
                intentUsageMetricsId={intentUsageMetricsId}
                sourceFrom={"LESSON_PLANNING_ASSISTANT"}
                showLikeButton={false}
                showDislikeButton={false}
              />
            </div>
          </div>
        ) : (
          <div className={contentContainerClass}>{content}</div>
        )}
        {shouldShowEditAndDeleteIcon ? renderEditAndDeleteIcon(id) : null}
      </div>
    </div>
  );
};

AITutorChat.defaultProps = {
  portalType: PLANNER,
};

const mapStateToProps = (state, ownProps) => {
  const { mode = "edit", canEdit = true, source } = ownProps;

  const shouldShowEditAndDeleteIcon =
    ACLStore.can("IdentityAccess:ShowEditIconInAITutor") ||
    ACLStore.can("Common:ShowEditIconInAITutor");

  const userType = getUserType(state);
  const curriculumType = getCurrentCurriculumProgramType(state);

  const isStudent = userType === "student";
  const isStaff = getIsStaff(state);

  const courseId =
    _.get(ownProps, "courseId") ||
    _.get(ownProps, "selectedCourse.courseId") ||
    _.get(state, "teacher.selected_class.selected_course");

  const isEditMode = mode === "edit" && canEdit;

  const isActivityLog = source === "ACTIVITY_LOG";

  const userInfo = getUserInfo();
  const userBasicDetails = getUserBasicDetailsFromCache({
    id: userInfo?.id,
    type: userInfo?.userEntityType,
  });

  return {
    userType,
    isStaff,
    isStudent,
    curriculumType,
    courseId,
    isEditMode,
    isActivityLog,
    shouldShowEditAndDeleteIcon,
    userBasicDetails,
  };
};

const mapActionCreators = {
  onSendQuery,
  evaluateAITutorConversation,
  updateAITutorMessage,
  getTextToSpeechAudio,
  hideIntercom,
  setGlobalTrayEnabledState,
};

export default compose(
  I18nHOC({ resource: ["ai", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  // withLoader,
  withPusherBind(["previewAIChatEvaluated"])
)(AITutorChat);
